import { computed, inject, Injectable, signal } from '@angular/core';
import { OrderItemRowTypeEnumRequestDto } from '@core/services/apiclient';
import CheckBox from 'devextreme/ui/check_box';
import dxTreeList, { SelectionChangedEvent } from 'devextreme/ui/tree_list';
import { catchError, EMPTY, finalize, map, Observable, Subject, take, takeUntil, tap } from 'rxjs';
import { mapToTreeListRow, OrderItemRow, TreeListRowData } from '../models/orders.model';
import { OrdersConfirmDialogService } from './orders-confirm-dialog.service';
import { OrdersSubscriptionGroupService } from './orders-subscription-group.service';
import { OrdersTreeDatasourceService } from './orders-tree-datasource.service';
import { OrdersService } from './orders.service';

interface ToggleOrderOptions {
  orderId: number;
  treeInstance: dxTreeList | undefined;
  includes: OrderItemRowTypeEnumRequestDto[];
}

@Injectable()
export class OrdersSelectionService {
  private readonly datasourceService = inject(OrdersTreeDatasourceService);
  private readonly ordersService = inject(OrdersService);
  private readonly dialogService = inject(OrdersConfirmDialogService);
  private readonly subscriptionGroupService = inject(OrdersSubscriptionGroupService);

  readonly isSelectAllValidationPending = signal<boolean>(false);
  private readonly apiTotalAmount = signal<number | null>(null);
  private readonly selectedOrdersIds = signal<number[]>([]);
  readonly selectedRows = signal<TreeListRowData[]>([]);
  readonly selectedFoldersIds = signal<number[]>([]);
  private readonly isRestoringSelectionFlag = signal<boolean>(false);
  private isSelectingParents = false;
  private readonly programmaticSelectedParentIds = new Set<number>();
  private readonly selectAllCancel$ = new Subject<void>();
  private selectAllRunId = 0;

  getIsRestoringSelection(): boolean {
    return this.isRestoringSelectionFlag();
  }

  /**
   * `selectedRows` can hold a group and its loaded children at once: DevExtreme reports both keys on
   * a recursive selection, and the group is only dropped again when its loaded children are known to
   * be all of them. Anything that sums the selection reads this instead, so a group and its children
   * are never counted twice.
   */
  private readonly effectiveSelection = computed(() =>
    this.subscriptionGroupService.normalizeSelection(this.selectedRows()),
  );

  readonly selectedTotalAmount = computed(() => {
    return (this.effectiveSelection() || []).reduce((acc, curr) => acc + (curr?.amount ?? 0), 0);
  });

  readonly selectedSeatsCount = computed(() =>
    this.effectiveSelection().reduce((acc, row) => acc + (row?.quantity ?? 0), 0),
  );

  readonly selectedFoldersCount = computed(() =>
    this.effectiveSelection().reduce((acc, row) => {
      if (row?.parentId != null) {
        return acc + 1;
      }
      return acc + (row?.orderItemCount ?? 1);
    }, 0),
  );
  readonly selectedRowsIds = computed(() => this.selectedRows().map((r) => r.id));

  readonly selectedOrdersCount = computed(() => this.selectedOrdersIds()?.length);

  private selectAllCheckboxElement: CheckBox | null = null;

  deselectAllRows(treeInstance: dxTreeList | undefined): void {
    this.selectAllRunId += 1;
    this.selectAllCancel$.next();
    if (treeInstance) {
      treeInstance.deselectAll();
      treeInstance.clearSelection();
      treeInstance.option('selectedRowKeys', []);
    }
    this.datasourceService.endSelectionOperation();
    this.isSelectAllValidationPending.set(false);
    this.apiTotalAmount.set(null);
    this.selectedOrdersIds.set([]);
    this.selectedRows.set([]);
    this.selectedFoldersIds.set([]);
    this.programmaticSelectedParentIds.clear();
  }

  setIsRestoringSelection(value: boolean): void {
    this.isRestoringSelectionFlag.set(value);
  }

  isParentPartiallySelected(parentId: number): boolean {
    return this.selectedRows().some((row) => row?.parentId === parentId);
  }

  syncSelectedParents(treeInstance: dxTreeList | undefined): void {
    if (!treeInstance || this.selectedRows().length === 0) return;

    const selectedRows = this.selectedRows();
    const parents = this.datasourceService.currentData().filter((row) => row?.hasItems);
    if (!parents.length) return;

    const currentKeys = this.toNumberArray(treeInstance.getSelectedRowKeys());
    const nextKeys = new Set<number>(currentKeys);
    // `orderItemCount` counts the items the list query returned, so an event session filter shrinks
    // it to the filtered session alongside the loaded children. Checking that single child would
    // then tick the group row, which reads as "the whole formula" while the action targets one item.
    const childrenAreComplete = this.datasourceService.areLoadedChildrenComplete();

    parents.forEach((parent) => {
      const total = parent.orderItemCount ?? 0;
      if (total <= 0) return;

      const selectedChildrenCount = selectedRows.filter((row) => row?.parentId === parent.id).length;
      const shouldSelectParent = childrenAreComplete && selectedChildrenCount >= total;

      if (shouldSelectParent) {
        nextKeys.add(parent.id);
        this.programmaticSelectedParentIds.add(parent.id);
        return;
      }
      if (this.programmaticSelectedParentIds.has(parent.id)) {
        nextKeys.delete(parent.id);
        this.programmaticSelectedParentIds.delete(parent.id);
      }
    });

    this.isSelectingParents = true;
    this.setIsRestoringSelection(true);
    treeInstance.option('selectedRowKeys', Array.from(nextKeys));
    this.setIsRestoringSelection(false);
    this.isSelectingParents = false;
  }

  private updateSelectedFoldersIds(): void {
    const selectedRowsData = this.subscriptionGroupService.normalizeSelection(this.selectedRows());
    const currentData = this.datasourceService.currentData();

    const folderIds = selectedRowsData.flatMap((row) => {
      if (row?.hasItems) {
        const childRows = currentData.filter((d) => d.parentId === row.id);
        return childRows?.length ? childRows.map((c) => c.id) : [row.id];
      }
      return [row.id];
    });

    this.selectedFoldersIds.set(folderIds);
    const uniqueOrderIds = [...new Set(selectedRowsData.map((row) => row?.orderId).filter(Boolean))];
    this.selectedOrdersIds.set(uniqueOrderIds);
  }

  toggleOrderFoldersByOrderNumber(options: ToggleOrderOptions): Observable<void> {
    const { treeInstance, orderId, includes } = options;
    if (!treeInstance) return EMPTY;

    if (this.isOrderSelected(orderId)) {
      return this.deselectOrderFolders(orderId, treeInstance);
    }

    return this.selectOrderFolders(orderId, treeInstance, includes);
  }

  private deselectOrderFolders(orderId: number, treeInstance: dxTreeList): Observable<void> {
    const selectedRowsData = (treeInstance.getSelectedRowsData() as TreeListRowData[]) ?? [];
    const sameOrderFolderIds = selectedRowsData
      .filter((folder) => folder?.orderId === orderId)
      .map(({ id }) => id);

    const currentSelectedRows = this.selectedRows();
    const remainingRows = currentSelectedRows.filter((row) => row?.orderId !== orderId);
    this.selectedRows.set(remainingRows);

    this.removeOrderFromSelection(orderId);
    treeInstance.deselectRows(sameOrderFolderIds);
    this.updateSelectedFoldersIds();
    return EMPTY;
  }

  private selectOrderFolders(
    orderId: number,
    treeInstance: dxTreeList,
    includes: OrderItemRowTypeEnumRequestDto[],
  ): Observable<void> {
    this.addOrderToSelection(orderId);
    this.datasourceService.startSelectionOperation('toggle-order');

    const filterRequest = this.datasourceService.buildFilterRequestForOrderNumber({
      orderId,
      includes,
    });

    return this.ordersService.getFiltredOrders(filterRequest).pipe(
      map(({ list }) => (list ?? []).map((row) => mapToTreeListRow(row as OrderItemRow))),
      tap((rows) => {
        const merged = this.mergeSelectionRows(this.selectedRows(), rows);
        this.setApiSelection(merged);
        const apiIds = rows.map((r) => r.id);
        const existingIds = this.getExistingTreeRowIds(treeInstance);

        const selectableIds = apiIds.filter((id) => existingIds.has(id));
        if (apiIds.length > 0) {
          treeInstance.selectRows(selectableIds, true);
        }
      }),
      map(() => void 0),
      catchError(() => {
        this.removeOrderFromSelection(orderId);
        this.datasourceService.endSelectionOperation();
        return EMPTY;
      }),
      finalize(() => {
        this.datasourceService.endSelectionOperation();
      }),
    );
  }
  updateSelectionFromEvent(event: SelectionChangedEvent): void {
    const { childrenByParentId, dataById, rowsById } = this.createSelectionContext();
    const selectedIds = this.toNumberArray(event.currentSelectedRowKeys);
    const deselectedIds = this.toNumberArray(event.currentDeselectedRowKeys);
    const selectedRowsData = Array.isArray(event.selectedRowsData)
      ? (event.selectedRowsData as TreeListRowData[])
      : [];

    selectedIds.forEach((id) => {
      if (rowsById.has(id)) return;
      const row = selectedRowsData.find((r) => r.id === id) ?? dataById.get(id);
      if (!row) return;

      this.handleSelectedRow(rowsById, row, childrenByParentId);
    });

    deselectedIds.forEach((id) => {
      const row = rowsById.get(id) ?? dataById.get(id);
      if (!row) return;

      this.handleDeselectedRow(rowsById, row, childrenByParentId);
    });

    this.removeFullySelectedParents(rowsById, childrenByParentId);
    this.commitSelection(rowsById);
  }

  private handleSelectedRow(
    rowsById: Map<number, TreeListRowData>,
    row: TreeListRowData,
    childrenByParentId: Map<number, TreeListRowData[]>,
  ): void {
    if (row.hasItems) {
      const children = childrenByParentId.get(row.id) ?? [];
      children.forEach((child) => rowsById.delete(child.id));
    }
    rowsById.set(row.id, row);
  }

  private handleDeselectedRow(
    rowsById: Map<number, TreeListRowData>,
    row: TreeListRowData,
    childrenByParentId: Map<number, TreeListRowData[]>,
  ): void {
    rowsById.delete(row.id);

    if (row.hasItems) {
      const children = childrenByParentId.get(row.id) ?? [];
      children.forEach((child) => rowsById.delete(child.id));
      return;
    }

    if (row.parentId != null) {
      const parentRow = rowsById.get(row.parentId);
      if (parentRow?.hasItems) {
        const children = childrenByParentId.get(row.parentId) ?? [];
        children.forEach((child) => rowsById.set(child.id, child));
        rowsById.delete(parentRow.id);
        rowsById.delete(row.id);
      } else {
        this.removeParentIfNoChildrenSelected(rowsById, row.parentId, childrenByParentId);
      }
    }
  }

  replaceParentWithChildrenIfSelected(parentId: number): void {
    const currentSelection = this.selectedRows();
    const parentRow = currentSelection.find((row) => row.id === parentId && row.hasItems);

    if (!parentRow) return;

    const currentData = this.datasourceService.currentData();
    const children = currentData.filter((row) => row.parentId === parentId);

    if (children.length === 0) return;

    const updatedSelection = currentSelection.filter((row) => row.id !== parentId);
    const merged = [...updatedSelection, ...children];
    this.selectedRows.set(merged);
    this.updateSelectedFoldersIds();
  }

  private createSelectionContext(): {
    childrenByParentId: Map<number, TreeListRowData[]>;
    dataById: Map<number, TreeListRowData>;
    rowsById: Map<number, TreeListRowData>;
  } {
    const currentData = this.datasourceService.currentData();
    return {
      childrenByParentId: this.indexChildrenByParentId(currentData),
      dataById: new Map<number, TreeListRowData>(currentData.map((row) => [row.id, row])),
      rowsById: new Map<number, TreeListRowData>(this.selectedRows().map((row) => [row.id, row])),
    };
  }

  private commitSelection(rowsById: Map<number, TreeListRowData>): void {
    const merged = Array.from(rowsById.values());
    this.selectedRows.set(merged);
    if (merged.length === 0) this.apiTotalAmount.set(null);
    this.updateSelectedFoldersIds();
  }

  private toNumberArray(value: unknown): number[] {
    if (!Array.isArray(value)) return [];
    return value.filter((v): v is number => typeof v === 'number');
  }

  private indexChildrenByParentId(rows: TreeListRowData[]): Map<number, TreeListRowData[]> {
    const map = new Map<number, TreeListRowData[]>();
    (rows ?? []).forEach((row) => {
      if (row.parentId == null) return;
      const existing = map.get(row.parentId) ?? [];
      map.set(row.parentId, [...existing, row]);
    });
    return map;
  }

  private removeParentIfNoChildrenSelected(
    rowsById: Map<number, TreeListRowData>,
    parentId: number,
    childrenByParentId: Map<number, TreeListRowData[]>,
  ): void {
    const remainingChildrenCount = Array.from(rowsById.values()).filter(
      (row) => row.parentId === parentId,
    ).length;

    if (remainingChildrenCount === 0) {
      rowsById.delete(parentId);
    }
  }

  private removeFullySelectedParents(
    rowsById: Map<number, TreeListRowData>,
    childrenByParentId: Map<number, TreeListRowData[]>,
  ): void {
    if (this.isSelectingParents) return;
    // Mirror of the promotion guarded in `syncSelectedParents`: with partially loaded children, the
    // group row keeps its own scope instead of collapsing to the single item that happens to be
    // loaded, so both entry points on that row act on the whole formula.
    if (!this.datasourceService.areLoadedChildrenComplete()) return;

    const selectedChildrenCountByParentId = new Map<number, number>();
    Array.from(rowsById.values()).forEach((row) => {
      if (row.parentId == null) return;
      selectedChildrenCountByParentId.set(
        row.parentId,
        (selectedChildrenCountByParentId.get(row.parentId) ?? 0) + 1,
      );
    });

    Array.from(rowsById.values()).forEach((row) => {
      if (!row.hasItems) return;
      const allChildrenCount = (childrenByParentId.get(row.id) ?? []).length;
      if (allChildrenCount === 0) return;
      const selectedChildrenCount = selectedChildrenCountByParentId.get(row.id) ?? 0;
      if (selectedChildrenCount === allChildrenCount) rowsById.delete(row.id);
    });
  }

  getIsSelectAllValidationPending(): boolean {
    return this.isSelectAllValidationPending();
  }

  isOrderSelected(orderId: number): boolean {
    return this.selectedOrdersIds().includes(orderId);
  }

  addOrderToSelection(orderId: number): void {
    this.selectedOrdersIds.update((ids) => (ids.includes(orderId) ? ids : [...ids, orderId]));
  }

  removeOrderFromSelection(orderId: number): void {
    this.selectedOrdersIds.update((ids) => ids.filter((id) => id !== orderId));
  }

  get isItemsCheckboxSelected(): boolean {
    return this.selectedFoldersIds().length > 0;
  }

  setSelectAllCheckbox(): void {
    this.selectAllCheckboxElement = CheckBox.getInstance(
      document.getElementsByClassName('dx-select-checkbox')?.[0],
    ) as CheckBox;
  }

  repaintSelectAllCheckbox(selectedCount: number, totalCount: number | undefined): void {
    if (this.selectAllCheckboxElement) {
      this.selectAllCheckboxElement['_props'].value = this.getSelectAllCheckboxState(
        selectedCount,
        totalCount,
      );
      this.selectAllCheckboxElement.repaint();
    }
  }

  getSelectAllCheckboxState(selectedCount: number, totalCount: number | undefined): boolean | undefined {
    if (!selectedCount) return false;
    if (typeof totalCount !== 'number' || totalCount <= 0) return undefined;
    return selectedCount >= totalCount ? true : undefined;
  }

  handleSelectAllCheckboxChange(
    treeInstance: dxTreeList | undefined,
    includes: OrderItemRowTypeEnumRequestDto[],
  ): void {
    if (!treeInstance) return;

    const totalCount = this.datasourceService.totalCount();
    const onCancel = () => {
      this.isSelectAllValidationPending.set(false);
      treeInstance.deselectAll();
    };

    const onValidate = () => {
      this.isSelectAllValidationPending.set(false);
      if (!this.datasourceService.isPaginatedMode()) {
        this.selectedRows.set((treeInstance.getSelectedRowsData() as TreeListRowData[]) ?? []);
        this.updateSelectedFoldersIds();
        return;
      }

      this.executeSelectAll(treeInstance, includes, totalCount, onCancel);
    };

    this.isSelectAllValidationPending.set(true);
    const canProceed = this.dialogService.handleSelectAllValidation(totalCount, onValidate, onCancel);
    if (canProceed) {
      onValidate();
    }
  }

  private executeSelectAll(
    treeInstance: dxTreeList,
    includes: OrderItemRowTypeEnumRequestDto[],
    totalCount: number,
    onCancel: () => void,
  ): void {
    this.selectAllRunId += 1;
    const runId = this.selectAllRunId;
    this.selectAllCancel$.next();
    this.datasourceService.startSelectionOperation('select-all');

    const filterRequest = this.datasourceService.buildFilterRequestForSelectAll({ includes, totalCount });
    this.ordersService
      .getFiltredOrders(filterRequest)
      .pipe(
        take(1),
        takeUntil(this.selectAllCancel$),
        catchError(() => {
          onCancel();
          this.deselectAllRows(treeInstance);
          return EMPTY;
        }),
      )
      .subscribe({
        next: (response) => {
          if (runId !== this.selectAllRunId) return;
          const list = Array.isArray(response?.list) ? response.list : [];
          const rows = (list as unknown as OrderItemRow[]) ?? [];
          const mappedRows = rows.map((row) => mapToTreeListRow(row));
          const ids = mappedRows.map((r) => r.id);
          this.apiTotalAmount.set(response?.totalAmount ?? null);
          this.setApiSelection(mappedRows);
          this.setIsRestoringSelection(true);
          if (ids?.length) {
            treeInstance.option('selectedRowKeys', ids);
          }
          this.datasourceService.endSelectionOperation();
          this.setIsRestoringSelection(false);
        },
      });
  }

  private setApiSelection(rows: TreeListRowData[]): void {
    const next = rows ?? [];
    const isSelectAll = this.datasourceService.activeSelectionOperation() === 'select-all';
    if (!isSelectAll) {
      this.apiTotalAmount.set(null);
    }
    this.selectedRows.set(next);
    this.datasourceService.mergeIntoCurrentData(next);
    this.selectedFoldersIds.set(next.map((r) => r.id));
    this.selectedOrdersIds.set([
      ...new Set(next.map((r) => r?.orderId).filter((id): id is number => typeof id === 'number')),
    ]);
  }

  private mergeSelectionRows(existing: TreeListRowData[], incoming: TreeListRowData[]): TreeListRowData[] {
    const byId = new Map<number, TreeListRowData>();
    (existing ?? []).forEach((row) => byId.set(row.id, row));
    (incoming ?? []).forEach((row) => byId.set(row.id, row));
    return Array.from(byId.values());
  }

  private getExistingTreeRowIds(treeInstance: dxTreeList): Set<number> {
    const rows = treeInstance.getVisibleRows();
    return new Set(rows.map((r) => r.key).filter((id) => id !== undefined && id !== null));
  }

  groupSelectedRowsByParent(): Record<number, TreeListRowData[]> {
    return this.selectedRows().reduce((acc, row) => {
      if (!row.parentId) return acc;
      if (!acc[row.parentId]) acc[row.parentId] = [];
      acc[row.parentId].push(row);
      return acc;
    }, {} as Record<number, TreeListRowData[]>);
  }
}
